import React, { useRef, useEffect } from 'react';
import { Box, Button, Slider } from '@mui/material';
import MarkerIcon from '../../../../assets/icons/Marker.svg';
import HighlighterIcon from '../../../../assets/icons/Highlighter.svg';
import SprayCanIcon from '../../../../assets/icons/SprayCan.svg';
import TextIcon from '../../../../assets/icons/TextTool.svg';
import EraserIcon from '../../../../assets/icons/Eraser.svg';
import UndoIcon from '../../../../assets/icons/Undo.svg';
import useIsLandscape from '../../../UIControls/useIsLandscape';

// CSS filter to turn a white SVG → #FF669B (pink) for icons on white background.
const PINK_FILTER = 'invert(52%) sepia(88%) saturate(450%) hue-rotate(294deg) brightness(101%)';
// CSS filter to turn any coloured SVG → white, used on the selected (pink bg) cell.
const WHITE_FILTER = 'brightness(0) invert(1)';

const BORDER_COLOR = 'rgba(255, 102, 155, 0.30)';
const OUTER_BORDER_COLOR = '#FF669B';
const PANEL_SIDE_PADDING = 4.75;
const SWATCH_GAP = 6.75;
const RECENT_SWATCH_SIZE = 20;
const TOOL_CELL_SIZE_DESKTOP = 82;
const TOOL_CELL_SIZE_MOBILE = 47;
const TEXT_TOOL_INDEX = 3;

/**
 * Tool definitions.
 * `pinkSvg: true` — SVG has a pink fill (SprayCan, Undo); needs WHITE_FILTER on selected cells,
 *                    no filter on unselected cells (already pink).
 * Default (no flag) — SVG has a white fill; needs PINK_FILTER on unselected cells,
 *                     no filter on selected cells (already white on pink bg).
 */
const TOOLS = [
    { value: 'pencil', icon: MarkerIcon, alt: 'Pencil' },
    { value: 'highlighter', icon: HighlighterIcon, alt: 'Highlighter' },
    { value: 'spray', icon: SprayCanIcon, alt: 'Spray', pinkSvg: true },
    { value: 'text', icon: TextIcon, alt: 'Text', pinkSvg: true },
    { value: 'eraser', icon: EraserIcon, alt: 'Eraser' },
];
const TOOL_STRIP_ITEM_COUNT = TOOLS.length + 1; // tools + undo

/**
 * WhiteboardToolbar
 *
 * Expanded layout (bare controls above the segmented white tool strip):
 *
 *   [recent colour swatches — white panel, 163×30px]  ← pencil / spray / text when history exists
 *   [colour swatch + slider / size number — white panel]
 *   [Save — right-aligned, hidden in combined mode]
 *   ──────────────────────────────
 *   [tool strip: segmented white bar with pink borders]
 *
 * Collapsed: tool strip only.
 *
 * Colour rules per tool:
 *   pencil      – picker enabled + recent swatches + thickness slider
 *   highlighter – picker disabled (locked yellow) + thickness slider
 *   spray       – picker enabled + recent swatches + thickness slider
 *   text        – picker enabled + recent swatches + size number input
 *   eraser      – no colour section, thickness slider only
 */
const WhiteboardToolbar = ({
    activeTool,
    setActiveTool,
    color,
    setColor,
    thickness,
    setThickness,
    onSave,
    onLoad, // reserved — see commented-out Load button below
    onReset,
    hasUnsavedChanges,
    isExpanded,
    setIsExpanded,
    recentColours = [],
    onPickRecentColour,
    showTextHelp = true, // retained for API compatibility; text-tips section removed
    isCombinedMode = false, // Phase 3: hides Save in combined mode
    isMobile = false,
}) => {
    const containerRef = useRef();
    const previousColorRef = useRef(color);
    const drawingThicknessRef = useRef(10); // shared between pencil, highlighter, spray
    const textSizeRef = useRef(isMobile ? 7 : 12); // remembered size for the text tool
    const isLandscape = useIsLandscape();

    // Enforce highlighter colour lock; restore per-tool thickness on tool switch.
    useEffect(() => {
        if (activeTool === 'highlighter') {
            if (color !== 'rgba(220,240,60,0.5)') {
                previousColorRef.current = color;
            }
            setColor('rgba(220,240,60,0.5)');
        } else {
            if (color === 'rgba(220,240,60,0.5)' && previousColorRef.current) {
                setColor(previousColorRef.current);
            }
        }
        if (['pencil', 'highlighter', 'spray'].includes(activeTool)) {
            setThickness(drawingThicknessRef.current);
        } else if (activeTool === 'text') {
            setThickness(textSizeRef.current);
        }
    }, [activeTool, setColor, color, setThickness]);

    useEffect(() => {}, [isLandscape]);

    // Collapse when clicking outside the toolbar or canvas.
    useEffect(() => {
        const handleClickOutside = (event) => {
            const isClickInsideToolbar = containerRef.current?.contains(event.target);
            const isCanvasClick =
                event.target?.closest('.whiteboard-canvas-wrapper') || event.target?.closest('.whiteboard-container');
            if (!isClickInsideToolbar && !isCanvasClick) {
                setIsExpanded(false);
            }
        };
        if (isExpanded) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isExpanded, setIsExpanded]);

    const handleToolChange = (newTool) => {
        if (!newTool || newTool === activeTool) return;
        if (['pencil', 'highlighter', 'spray'].includes(activeTool)) {
            drawingThicknessRef.current = thickness;
        } else if (activeTool === 'text') {
            textSizeRef.current = thickness;
        }
        setActiveTool(newTool);
        setIsExpanded(true);
    };

    const showColourSection = activeTool !== 'eraser';
    const colourDisabled = activeTool === 'highlighter';
    const showRecentColours = ['pencil', 'spray', 'text'].includes(activeTool);
    const colourDisplayValue = activeTool === 'highlighter' ? '#DCF03C' : color;
    const toolCellSize = isMobile ? TOOL_CELL_SIZE_MOBILE : TOOL_CELL_SIZE_DESKTOP;
    const toolStripWidth = toolCellSize * TOOL_STRIP_ITEM_COUNT;
    const textPanelOffsetLeft = TEXT_TOOL_INDEX * toolCellSize + (toolCellSize - 81) / 2;
    const selectedInset = isMobile ? 4 : 10;
    const iconSize = isMobile ? 24 : 40;

    /** Colour swatch box — clickable colour picker. */
    const renderColourSwatch = () => (
        <Box
            sx={{
                width: `${RECENT_SWATCH_SIZE}px`,
                height: `${RECENT_SWATCH_SIZE}px`,
                borderRadius: '3px',
                border: '1px solid #ECECEC',
                backgroundColor: colourDisplayValue,
                position: 'relative',
                overflow: 'hidden',
                flexShrink: 0,
            }}
        >
            <input
                type="color"
                value={colourDisplayValue}
                onChange={(e) => {
                    if (!colourDisabled) {
                        setColor(e.target.value);
                        previousColorRef.current = e.target.value;
                    }
                }}
                disabled={colourDisabled}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: 0,
                    padding: 0,
                    margin: 0,
                    background: 'transparent',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    opacity: 0,
                    cursor: colourDisabled ? 'not-allowed' : 'pointer',
                }}
            />
        </Box>
    );

    /**
     * Controls panels — float bare above the gradient pill with no extra wrapper.
     *
     * Panel 1 (when recent colours exist): white card, 163×30.373px, border-radius 8.099px.
     * Panel 2: white card, 163×auto, same radius — colour swatch + slider or size number.
     *   Slider sits inside a grey track: 127.565×20.248px, border-radius 3.857px, bg #E5E5E5.
     * Text tool panel: 81px wide, 30px tall, border-radius 8px.
     */
    const renderControls = () => {
        const panelSx = {
            background: '#FFF',
            borderRadius: '8.099px',
            px: `${PANEL_SIDE_PADDING}px`,
            py: '5px',
            mb: '6px',
            width: '163px',
            boxSizing: 'border-box',
        };

        return (
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 'calc(100% + 8px)',
                    left: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    width: `${toolStripWidth}px`,
                }}
            >
                {/* Panel 1 — recent colour swatches */}
                {showRecentColours && recentColours?.length > 0 && (
                    <Box
                        sx={{
                            ...panelSx,
                            height: '30.373px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: `${SWATCH_GAP}px`,
                            overflow: 'hidden',
                        }}
                    >
                        {recentColours.slice(0, 6).map((c) => (
                            <Box
                                key={`${activeTool}-${c}`}
                                onClick={() => onPickRecentColour?.(c)}
                                title={c}
                                sx={{
                                    width: RECENT_SWATCH_SIZE,
                                    height: RECENT_SWATCH_SIZE,
                                    borderRadius: '3px',
                                    border: '1px solid #ECECEC',
                                    cursor: 'pointer',
                                    backgroundColor: c,
                                    flexShrink: 0,
                                }}
                            />
                        ))}
                    </Box>
                )}

                {/* Panel 2 — colour swatch + slider / size number */}
                {activeTool === 'text' ? (
                    /* Text tool: narrower panel (81px) with colour + size number */
                    <Box
                        sx={{
                            background: '#FFF',
                            borderRadius: '8px',
                            px: '6px',
                            mb: '6px',
                            width: '81px',
                            height: '30px',
                            boxSizing: 'border-box',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '3px',
                            ml: `${textPanelOffsetLeft}px`,
                        }}
                    >
                        {renderColourSwatch()}
                        <input
                            type="number"
                            value={thickness}
                            min={1}
                            max={72}
                            onChange={(e) => {
                                const v = Math.min(72, Math.max(1, Number(e.target.value) || 1));
                                setThickness(v);
                                textSizeRef.current = v;
                            }}
                            style={{
                                width: '46px',
                                height: '20px',
                                border: '1.9px solid #ECECEC',
                                borderRadius: '3px',
                                outline: 'none',
                                padding: '0 4px',
                                fontFamily: 'Inter, sans-serif',
                                fontSize: 13,
                                textAlign: 'center',
                            }}
                        />
                    </Box>
                ) : activeTool !== 'eraser' ? (
                    /* All other tools: 163px panel with colour swatch + grey slider track */
                    <Box
                        sx={{
                            ...panelSx,
                            height: '30.373px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        {showColourSection && renderColourSwatch()}

                        {/* Grey slider track */}
                        <Box
                            sx={{
                                width: '127.565px',
                                height: '20.248px',
                                borderRadius: '3.857px',
                                opacity: 0.6,
                                background: '#E5E5E5',
                                display: 'flex',
                                alignItems: 'center',
                                px: '4px',
                                flexShrink: 0,
                            }}
                        >
                            <Slider
                                value={thickness}
                                onChange={(e, newValue) => {
                                    setThickness(newValue);
                                    if (['pencil', 'highlighter', 'spray'].includes(activeTool)) {
                                        drawingThicknessRef.current = newValue;
                                    }
                                }}
                                min={1}
                                max={20}
                                sx={{
                                    width: '100%',
                                    py: 0,
                                    mt: 0,
                                    color: '#FF669B',
                                    '& .MuiSlider-thumb': {
                                        width: 14,
                                        height: 14,
                                        backgroundColor: '#FF669B',
                                        border: 'none',
                                        boxShadow: '0px 3px 10px rgba(0, 0, 0, 0.18)',
                                    },
                                    '& .MuiSlider-rail': {
                                        opacity: 1,
                                        backgroundColor: 'rgba(255, 102, 155, 0.30)',
                                        borderRadius: '120.431px',
                                    },
                                    '& .MuiSlider-track': {
                                        border: 'none',
                                        backgroundColor: '#FF669B',
                                        borderRadius: '120.431px',
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                ) : null}

                {/* Save button — hidden in combined mode (Phase 3) */}
                {!isCombinedMode && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: `${toolStripWidth}px` }}>
                        <Button variant="contained" size="small" onClick={onSave} disabled={!hasUnsavedChanges}>
                            Save
                        </Button>
                    </Box>
                )}

                {/*
                 * Load button — loads a previously saved whiteboard drawing.
                 * Uncomment below and wire up the `onLoad` prop in the parent to restore.
                 *
                 * <Button
                 *     variant="outlined"
                 *     size="small"
                 *     onClick={onLoad}
                 *     sx={{ border: '1px solid rgb(255, 102, 155)', color: 'rgb(255, 102, 155)', fontWeight: 500, borderRadius: '8px' }}
                 * >
                 *     Load
                 * </Button>
                 */}
            </Box>
        );
    };

    /**
     * Segmented tool strip — white background with pink (#FF669B 30%) borders.
     * Selected cell: solid #FF669B background, 66×66px, border-radius 12.878px.
     * White SVG icons: PINK_FILTER on unselected cells, no filter on selected (already white on pink).
     * Pink SVG icons (SprayCan, Undo): no filter on unselected, WHITE_FILTER on selected.
     */
    const renderToolStrip = () => {
        const allItems = [...TOOLS, { value: '__undo__', icon: UndoIcon, alt: 'Reset', pinkSvg: true, isAction: true }];

        return (
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    border: `3px solid ${OUTER_BORDER_COLOR}`,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    background: '#FFF',
                }}
            >
                {allItems.map((item, idx) => {
                    const isSelected = item.value === activeTool;
                    const isFirst = idx === 0;
                    const isLast = idx === allItems.length - 1;

                    let iconFilter;
                    if (isSelected) {
                        // Selected pink bg: white SVGs need no filter, pink SVGs → white
                        iconFilter = item.pinkSvg ? WHITE_FILTER : undefined;
                    } else {
                        // Unselected white bg: white SVGs → pink, pink SVGs need no filter
                        iconFilter = item.pinkSvg ? undefined : PINK_FILTER;
                    }

                    const baseBorderRadius = isFirst ? '15px 0 0 15px' : isLast ? '0 15px 15px 0' : '0';

                    return (
                        <Box
                            key={item.value}
                            component="button"
                            onClick={() => (item.isAction ? onReset?.() : handleToolChange(item.value))}
                            title={item.alt}
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: `${toolCellSize}px`,
                                height: `${toolCellSize}px`,
                                border: 'none',
                                borderRight: isLast ? 'none' : `1px solid ${BORDER_COLOR}`,
                                borderRadius: baseBorderRadius,
                                background: '#FFF',
                                cursor: 'pointer',
                                padding: 0,
                                flexShrink: 0,
                                '&:hover': {
                                    background: isSelected ? '#FFF' : 'rgba(255, 102, 155, 0.08)',
                                },
                            }}
                        >
                            {isSelected && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: selectedInset,
                                        left: selectedInset,
                                        right: selectedInset,
                                        bottom: selectedInset,
                                        borderRadius: '12.878px',
                                        background: '#FF669B',
                                    }}
                                />
                            )}
                            <img
                                src={item.icon}
                                alt={item.alt}
                                style={{
                                    position: 'relative',
                                    zIndex: 1,
                                    width: iconSize,
                                    height: iconSize,
                                    filter: iconFilter,
                                    display: 'block',
                                }}
                            />
                        </Box>
                    );
                })}
            </Box>
        );
    };

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'sticky',
                top: isLandscape ? '0px' : '100px',
                zIndex: 999,
                width: `${toolStripWidth}px`,
                filter: 'drop-shadow(1px 0px 5px rgba(0,0,0,0.12))',
            }}
        >
            {/* Expanded: compact bare controls above the tool strip */}
            {isExpanded && renderControls()}

            {/* Tool strip — visible in both collapsed and expanded states */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: isLandscape ? 'flex-start' : 'center',
                }}
            >
                {renderToolStrip()}
            </Box>
        </Box>
    );
};

export default WhiteboardToolbar;
